import { mapClientWritePayload, type ClientWritePayload } from '@/api/clients/clientsApi.types';

export interface NextVisitApiResource {
  id: string;
  date: string;
  start_time: string;
  end_time: string;
  therapy_type: string;
  status: string;
}

export interface NextVisit {
  id: string;
  date: string;
  startTime: string;
  therapyType: string;
  status: string;
}

export interface LastVisitApiResource {
  date: string;
  start_time: string;
  therapy_type: string | null;
  source: string;
}

export interface LastVisit {
  date: string;
  startTime: string;
  therapyType: string | null;
}

export interface ActivePlanSummaryApiResource {
  id: string;
  version: number;
  status: string;
  created_at: string;
  client_visible: boolean;
}

export interface ActivePlanSummary {
  id: string;
  version: number;
  status: string;
  createdAt: string;
  clientVisible: boolean;
}

export interface PatientTimelineEventApiResource {
  id: string;
  kind: string;
  event_date: string;
  event_time: string | null;
  therapy_type: string | null;
  status: string | null;
  note: string | null;
  consent_type: string | null;
  consent_decision: string | null;
  consent_version: string | null;
  file_name: string | null;
  file_visibility: string | null;
  message_type: string | null;
  message_channel: string | null;
  message_status: string | null;
}

export interface PatientTimelinePageApiResource {
  items: PatientTimelineEventApiResource[];
  next_cursor: string | null;
}

export interface PatientTimelinePage {
  items: PatientTimelineEvent[];
  nextCursor: string | null;
}

export interface PatientTimelineEvent {
  id: string;
  kind: string;
  eventDate: string;
  eventTime: string | null;
  therapyType: string | null;
  status: string | null;
  note: string | null;
  consentType: string | null;
  consentDecision: string | null;
  consentVersion: string | null;
  fileName: string | null;
  fileVisibility: string | null;
  messageType: string | null;
  messageChannel: string | null;
  messageStatus: string | null;
}

export interface PatientApiResource {
  id: string;
  client_id: string | null;
  name: string;
  species_code: string | null;
  species: string | null;
  breed: string | null;
  sex: string | null;
  date_of_birth: string | null;
  weight: string | null;
  neutered: boolean | null;
  main_issue: string | null;
  diagnosis: string | null;
  medication: string | null;
  past_surgeries: string | null;
  allergies: string | null;
  notes: string | null;
  warning_codes: string[];
  warnings: string[];
  photo_url: string | null;
  status: string;
  next_visit: NextVisitApiResource | null;
  last_visit: LastVisitApiResource | null;
  active_plan: ActivePlanSummaryApiResource | null;
  treatment_clearance: string | null;
}

export interface PatientSummaryApiResource {
  id: string;
  client_id: string | null;
  name: string;
  species_code: string | null;
  species: string | null;
  breed: string | null;
  sex: string | null;
  date_of_birth: string | null;
  warning_codes: string[];
  warnings: string[];
  photo_url: string | null;
  status: string;
}

export interface Patient {
  id: string;
  clientId: string | null;
  name: string;
  speciesCode: string | null;
  species: string | null;
  breed: string | null;
  sex: string | null;
  dateOfBirth: string | null;
  weight: string | null;
  neutered: boolean | null;
  mainIssue: string | null;
  diagnosis: string | null;
  medication: string | null;
  pastSurgeries: string | null;
  allergies: string | null;
  notes: string | null;
  warningCodes: string[];
  warnings: string[];
  photoUrl: string | null;
  status: string;
  nextVisit: NextVisit | null;
  lastVisit: LastVisit | null;
  activePlan: ActivePlanSummary | null;
  treatmentClearance: string | null;
}

export interface PatientSummary {
  id: string;
  clientId: string | null;
  name: string;
  speciesCode: string | null;
  species: string | null;
  breed: string | null;
  sex: string | null;
  dateOfBirth: string | null;
  warningCodes: string[];
  warnings: string[];
  photoUrl: string | null;
  status: string;
}

export type PatientOwnerChoice =
  | { kind: 'later' }
  | { kind: 'existing'; clientId: string }
  | { kind: 'new'; client: ClientWritePayload };

export function mapPatientOwnerChoice(
  choice: Exclude<PatientOwnerChoice, { kind: 'later' }>
): Record<string, unknown> {
  if (choice.kind === 'existing') {
    return { existing_client_id: choice.clientId };
  }
  return { new_client: mapClientWritePayload(choice.client) };
}

export interface PatientWritePayload {
  name: string;
  speciesCode: string | null;
  breed: string | null;
  sex: string | null;
  dateOfBirth: string | null;
  weight: string | null;
  neutered: boolean | null;
  mainIssue: string | null;
  diagnosis: string | null;
  medication: string | null;
  pastSurgeries: string | null;
  allergies: string | null;
  warningCodes: string[];
}

export function mapApiResourceToPatient(resource: PatientApiResource): Patient {
  return {
    id: resource.id,
    clientId: resource.client_id,
    name: resource.name,
    speciesCode: resource.species_code,
    species: resource.species,
    breed: resource.breed,
    sex: resource.sex,
    dateOfBirth: resource.date_of_birth,
    weight: resource.weight,
    neutered: resource.neutered,
    mainIssue: resource.main_issue,
    diagnosis: resource.diagnosis,
    medication: resource.medication,
    pastSurgeries: resource.past_surgeries,
    allergies: resource.allergies,
    notes: resource.notes,
    warningCodes: resource.warning_codes,
    warnings: resource.warnings,
    photoUrl: resource.photo_url,
    status: resource.status,
    nextVisit: mapNextVisit(resource.next_visit),
    lastVisit: mapLastVisit(resource.last_visit),
    activePlan: mapActivePlan(resource.active_plan),
    treatmentClearance: resource.treatment_clearance,
  };
}

function mapActivePlan(resource: ActivePlanSummaryApiResource | null): ActivePlanSummary | null {
  if (resource === null) {
    return null;
  }
  return {
    id: resource.id,
    version: resource.version,
    status: resource.status,
    createdAt: resource.created_at,
    clientVisible: resource.client_visible,
  };
}

export function mapApiResourceToPatientTimelineEvent(
  resource: PatientTimelineEventApiResource
): PatientTimelineEvent {
  return {
    id: resource.id,
    kind: resource.kind,
    eventDate: resource.event_date,
    eventTime: resource.event_time,
    therapyType: resource.therapy_type,
    status: resource.status,
    note: resource.note,
    consentType: resource.consent_type,
    consentDecision: resource.consent_decision,
    consentVersion: resource.consent_version,
    fileName: resource.file_name,
    fileVisibility: resource.file_visibility,
    messageType: resource.message_type,
    messageChannel: resource.message_channel,
    messageStatus: resource.message_status,
  };
}

export function mapApiResourceToPatientTimelinePage(
  resource: PatientTimelinePageApiResource
): PatientTimelinePage {
  return {
    items: resource.items.map(mapApiResourceToPatientTimelineEvent),
    nextCursor: resource.next_cursor,
  };
}

function mapLastVisit(resource: LastVisitApiResource | null): LastVisit | null {
  if (resource === null) {
    return null;
  }
  return {
    date: resource.date,
    startTime: resource.start_time,
    therapyType: resource.therapy_type,
  };
}

function mapNextVisit(resource: NextVisitApiResource | null): NextVisit | null {
  if (resource === null) {
    return null;
  }
  return {
    id: resource.id,
    date: resource.date,
    startTime: resource.start_time,
    therapyType: resource.therapy_type,
    status: resource.status,
  };
}

export function mapApiResourceToPatientSummary(
  resource: PatientSummaryApiResource
): PatientSummary {
  return {
    id: resource.id,
    clientId: resource.client_id,
    name: resource.name,
    speciesCode: resource.species_code,
    species: resource.species,
    breed: resource.breed,
    sex: resource.sex,
    dateOfBirth: resource.date_of_birth,
    warningCodes: resource.warning_codes,
    warnings: resource.warnings,
    photoUrl: resource.photo_url,
    status: resource.status,
  };
}

export interface OwnerPetApiResource {
  id: string;
  name: string;
  species_code: string | null;
  species: string | null;
  breed: string | null;
  sex: string | null;
  date_of_birth: string | null;
  photo_url: string | null;
  status: string;
}

export interface OwnerPet {
  id: string;
  name: string;
  speciesCode: string | null;
  species: string | null;
  breed: string | null;
  sex: string | null;
  dateOfBirth: string | null;
  photoUrl: string | null;
  status: string;
}

export function mapApiResourceToOwnerPet(resource: OwnerPetApiResource): OwnerPet {
  return {
    id: resource.id,
    name: resource.name,
    speciesCode: resource.species_code,
    species: resource.species,
    breed: resource.breed,
    sex: resource.sex,
    dateOfBirth: resource.date_of_birth,
    photoUrl: resource.photo_url,
    status: resource.status,
  };
}

export function mapPatientWritePayload(payload: PatientWritePayload): Record<string, unknown> {
  return {
    name: payload.name,
    species: payload.speciesCode,
    breed: payload.breed,
    sex: payload.sex,
    date_of_birth: payload.dateOfBirth,
    weight: payload.weight,
    neutered: payload.neutered,
    main_issue: payload.mainIssue,
    diagnosis: payload.diagnosis,
    medication: payload.medication,
    past_surgeries: payload.pastSurgeries,
    allergies: payload.allergies,
    warnings: payload.warningCodes,
  };
}
